import {
  Box,
  Card,
  Grid,
  Skeleton
} from '@mui/material';
import OrganizationBanner from 'components/OrganizationBanner';
import OrganizationCharts from 'components/OrganizationCharts';
import RevisionsByUserChart from 'components/RevisionsByUserChart';
import { endOfYear, startOfYear } from 'date-fns';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { getOrganizationAdminDetails } from 'services/admin-organizations';
import { getOrganizationInvites, getOrganizationMembers } from 'services/admin-users';
import { TabPanelProps } from 'types/admin';

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Skeleton Loading Component
const OrganizationDetailsSkeleton: React.FC = () => {
  return (
    <Box sx={{ py: 3 }}>
      {/* Organization Banner Skeleton */}
      <Box sx={{ mb: 3 }}>
        <Card sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Skeleton variant="circular" width={60} height={60} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="40%" height={24} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Skeleton variant="rectangular" width={80} height={36} />
              <Skeleton variant="rectangular" width={80} height={36} />
              <Skeleton variant="rectangular" width={80} height={36} />
            </Box>
          </Box>
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item}>
                <Box sx={{ textAlign: 'center' }}>
                  <Skeleton variant="text" width="40%" height={20} sx={{ mx: 'auto' }} />
                  <Skeleton variant="text" width="60%" height={32} sx={{ mx: 'auto' }} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>
      </Box>

      {/* Charts Skeleton */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} md={4} key={item}>
              <Card sx={{ height: 300, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Skeleton variant="text" width={80} height={24} />
                  <Skeleton variant="text" width={60} height={20} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Skeleton variant="text" width={100} height={16} />
                  <Box>
                    <Skeleton variant="text" width={60} height={16} />
                    <Skeleton variant="text" width={60} height={16} />
                  </Box>
                </Box>
                <Skeleton variant="rectangular" width="100%" height={200} />
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Revisions Chart Skeleton */}
      <Box sx={{ mb: 3 }}>
        <Card sx={{ height: 400, p: 2 }}>
          <Skeleton variant="text" width={200} height={24} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" width="100%" height={320} />
        </Card>
      </Box>

      {/* Search and Tabs Skeleton */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Skeleton variant="rectangular" width="100%" height={50} />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Skeleton variant="rectangular" width={200} height={48} />
      </Box>

      {/* Table Skeleton */}
      <Box>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    </Box>
  );
};

const AdminOrganizationDetails: React.FC = () => {
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortInvitesOrder, setSortInvitesOrder] = useState<'asc' | 'desc'>('desc');
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortInvitesBy, setSortInvitesBy] = useState('updatedAt');
  const [searchEmail, setSearchEmail] = useState<string>('');
  const [period, setPeriod] = useState<'week' | 'two_weeks' | 'month' | 'quarter' | 'semester' | 'year'>('month');
  const [startDate, setStartDate] = useState<Date>(() => {
    return startOfYear(new Date()); // Default to start of current year
  });
  const [endDate, setEndDate] = useState<Date>(() => {
    return endOfYear(new Date()); // Default to end of current year
  });

  // New query to get full organization details
  const { data: organizationDetails, refetch: refetchOrganizationDetails, isLoading: isLoadingOrganization } = useQuery(
    ['organizationDetails', organizationId, period],
    () => getOrganizationAdminDetails(organizationId, period),
    { enabled: !!organizationId }
  );

  const { data: membersData, isLoading: isLoadingMembers, refetch: refetchMembers } = useQuery(
    ['members', page, rowsPerPage, sortBy, sortOrder, organizationId, searchEmail],
    () => getOrganizationMembers({
      page: page + 1,
      pageSize: rowsPerPage,
      sortBy,
      sortOrder,
      searchOrganization: organizationId,
      searchType: 'organization',
      searchEmail
    }),
    { enabled: !!organizationId }
  );

  const { data: invitesData, isLoading: isLoadingInvites, refetch: refetchInvites } = useQuery(
    ['invites', page, rowsPerPage, sortInvitesBy, sortInvitesOrder, organizationId, searchEmail],
    () => getOrganizationInvites({
      page: page + 1,
      pageSize: rowsPerPage,
      sortBy: sortInvitesBy,
      sortOrder: sortInvitesOrder,
      searchOrganization: organizationId,
      searchType: 'organization',
      searchEmail
    }),
    { enabled: !!organizationId }
  );

  // Check if any data is loading
  const isLoading = isLoadingOrganization || isLoadingMembers || isLoadingInvites;

  const handleRequestSort = (event: React.MouseEvent<unknown>, newOrderBy: string, type: 'members' | 'invites') => {
    if (type === 'members') {
      const isAsc = sortBy === newOrderBy && sortOrder === 'asc';
      setSortOrder(isAsc ? 'desc' : 'asc');
      setSortBy(newOrderBy);
      setPage(0);
    } else {
      const isAsc = sortInvitesBy === newOrderBy && sortInvitesOrder === 'asc';
      setSortInvitesOrder(isAsc ? 'desc' : 'asc');
      setSortInvitesBy(newOrderBy);
      setPage(0);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handlePeriodChange = (newPeriod: 'week' | 'two_weeks' | 'month' | 'quarter' | 'semester' | 'year') => {
    setPeriod(newPeriod);
  };

  if (!organizationId) {
    return <div>Organization ID not found</div>;
  }

  // Show skeleton while loading
  if (isLoading) {
    return <OrganizationDetailsSkeleton />;
  }

  return (
    <Box sx={{ py: 3 }}>
      {/* Organization Banner */}
      {organizationDetails && (
        <OrganizationBanner
          organization={organizationDetails}
          onPeriodChange={handlePeriodChange}
        />
      )}

      {/* Organization Charts */}
      {organizationDetails && (
        <Box sx={{ mb: 3 }}>
          <OrganizationCharts organization={organizationDetails} />
        </Box>
      )}

      {/* Revisions by User Chart */}
      {membersData?.data && membersData.data.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <RevisionsByUserChart members={membersData.data} />
        </Box>
      )}

    </Box>
  );
};

export default AdminOrganizationDetails; 